"use client"
import { zodResolver } from "@hookform/resolvers/zod"
import { useState } from "react"
import { useForm } from "react-hook-form"
import SimpleModal from "../../Modal/BaseModal"
import { User } from "@/app/generated/prisma"
import { apiService } from "@/lib/api"
import { content, states } from "@/lib/content/auth"
import { Routes } from "@/lib/routes"
import { sendUserDataToMautic } from "@/services/api/useApi"
import { handleRedirect } from "@/utils/auth-redirect"
import {
   CompleteProfileFormInputs,
   CompleteProfileFormSchema,
} from "@/utils/helpers/validators/auth/CompleteProfileFormSchema"
import { add } from "date-fns"
import { useSession } from "next-auth/react"
import { useRouter, useSearchParams } from "next/navigation"
import { toast } from "sonner"
import { match } from "ts-pattern"

const CompleteProfile = () => {
   const { data: session } = useSession()
   const user: Partial<User> | null = session?.user || null
   const searchParams = useSearchParams()
   const router = useRouter()
   const user_group = searchParams.get("user_group")
   const redirectUrl = searchParams.get("redirect")
   const callbackUrl = searchParams.get("callbackUrl")
   const resourceName = searchParams.get("resource")

   const handleDownloadResource = (resourceName: string | null) => {
      if (!resourceName) return

      // Create a temporary anchor element to trigger download
      const link = document.createElement("a")
      link.href = `/library/${resourceName}.pdf`
      link.download = `${resourceName}.pdf`

      const promo = document.createElement("a")
      promo.href = `/library/Restaurant-Monthly-Promo-Calendar.pdf`
      promo.download = `Restaurant-Monthly-Promo-Calendar.pdf`

      document.body.appendChild(link)
      document.body.appendChild(promo)
      link.click()
      promo.click()
      // Remove the elements after clicking
      link.remove()
      promo.remove()
   }

   const {
      register,
      handleSubmit,
      formState: { errors, isLoading, isSubmitting },
   } = useForm<CompleteProfileFormInputs>({
      resolver: zodResolver(CompleteProfileFormSchema),
      values: {
         firstName: user?.firstName || "",
         lastName: user?.lastName || "",
         company: user?.company || "",
         companyUrl: user?.website || "",
         phone: user?.phone || "",
         position: user?.title || "",
         city: user?.city || "",
         stateProvince: user?.state || "",
         companyOperations: user?.companyOperations || "",
         companyDescription: user?.companyDescription || "",
      },
      resetOptions: {
         keepDirtyValues: true,
      },
   })

   const [completed, setCompleted] = useState(false)

   const updateToAllSites = async ({ form_data }: { form_data: CompleteProfileFormInputs }) => {
      const user_groupVal = match(user_group)
         .with("job-seeker", () => "JobSeeker")
         .with("employer", () => "Employer")
         .otherwise(() => "Employer")

      form_data = {
         ...form_data,
         user_group_id: user_groupVal,
         email: user?.email,
         id: user?.id,
      }

      // Simulate an API call to update the profile on all sites
      const res = await apiService.post<any>(Routes.api.unifiedAccountCompleteApi.getPath(), form_data)
      return res
   }

   const onSubmit = async (form_data: CompleteProfileFormInputs) => {
      const data = await updateToAllSites({ form_data })

      if (data?.user) {
         await sendUserDataToMautic({
            data: {
               firstName: form_data.firstName,
               lastName: form_data.lastName,
               company: form_data.company,
               email: user?.email || "",
               phone: form_data.phone,
               password: "",
               city: form_data.city,
               state: form_data.stateProvince,
               position: form_data.position,
               companyOperations: form_data.companyOperations,
               companyDescription: form_data.companyDescription,
               website: form_data.companyUrl || "",
            },
         })

         // Fire-and-forget: tag Sign_up + Pending_Verification in Mautic,
         // generate a magic link, and patch the Mautic contact — Mautic then
         // sends the verification email via campaign automation.
         if (user?.email && !user?.emailVerified) {
            fetch("/api/unified/request-verification", {
               method: "POST",
               headers: { "Content-Type": "application/json" },
               body: JSON.stringify({ email: user.email }),
            }).catch(() => {})
         }

         console.log(data)

         const token = {
            training_session: { token: data?.training ?? "", exp: add(new Date(), { minutes: 30 }) },
            jobs_session: { token: data?.jobs ?? "", exp: add(new Date(), { minutes: 30 }) },
            webinars_session: { token: data?.webinars ?? "", exp: add(new Date(), { minutes: 30 }) },
         }

         const destination = handleRedirect({ redirectUrl, tokens: token, callbackUrl, defaultRedirect: "" })

         handleDownloadResource(resourceName)

         setCompleted(true)

         toast.success("Profile updated successfully!")
         if (destination) {
            router.push(destination)
            router.refresh()
         }
      } else {
         toast.error("Failed to update profile.")
      }
   }

   if (!user || completed) {
      return null
   }

   return (
      <SimpleModal
         title="Complete Your Profile"
         description="Please fill out all required fields to complete and validate your profile."
         onClose={() => {}}
         isOpen={true}
         skippable={false}
      >
         <form onSubmit={handleSubmit(onSubmit)}>
            <div className="mb-9 mt-2 flex flex-col gap-1 space-y-4 md:gap-2 lg:gap-3">
               <div className="profile-complete flex flex-col gap-1 md:flex-row md:gap-2 lg:gap-4">
                  <div className="mb-2 flex-1 md:mb-0">
                     <label className="mb-1 block text-start text-xs font-medium text-gray-700" htmlFor="firstName">
                        First Name
                     </label>
                     <input
                        id="firstName"
                        {...register("firstName", { required: "First name is required" })}
                        className={`block w-full rounded-md border px-3 py-2 text-sm text-gray-900 placeholder-gray-400 focus:border-orange-500 focus:outline-none focus:ring-2 focus:ring-orange-500 md:text-base ${
                           errors.firstName
                              ? "border-red-500 focus:border-red-500 focus:ring-red-500"
                              : "border-gray-300"
                        }`}
                        placeholder="First Name"
                        autoComplete="given-name"
                        aria-invalid={errors.firstName ? "true" : "false"}
                        aria-describedby={errors.firstName ? "firstName-error" : undefined}
                     />
                     {errors.firstName && (
                        <span id="firstName-error" className="mt-1 text-left text-sm text-red-500" role="alert">
                           {errors.firstName.message}
                        </span>
                     )}
                  </div>
                  <div className="flex-1">
                     <label className="mb-1 block text-start text-xs font-medium text-gray-700" htmlFor="lastName">
                        Last Name
                     </label>
                     <input
                        id="lastName"
                        {...register("lastName", { required: "Last name is required" })}
                        className={`block w-full rounded-md border px-3 py-2 text-sm text-gray-900 placeholder-gray-400 focus:border-orange-500 focus:outline-none focus:ring-2 focus:ring-orange-500 md:text-base ${
                           errors.lastName
                              ? "border-red-500 focus:border-red-500 focus:ring-red-500"
                              : "border-gray-300"
                        }`}
                        placeholder="Last Name"
                        autoComplete="family-name"
                        aria-invalid={errors.lastName ? "true" : "false"}
                        aria-describedby={errors.lastName ? "lastName-error" : undefined}
                     />
                     {errors.lastName && (
                        <span id="lastName-error" className="mt-1 text-left text-sm text-red-500" role="alert">
                           {errors.lastName.message}
                        </span>
                     )}
                  </div>
               </div>
               <div className=" flex gap-4">
                  <div className="flex-1">
                     <label className="mb-1 block text-start text-xs font-medium text-gray-700" htmlFor="company">
                        Company
                     </label>
                     <input
                        id="company"
                        {...register("company", { required: "Company is required" })}
                        className={`block w-full rounded-md border px-3 py-2 text-sm text-gray-900 placeholder-gray-400 focus:border-orange-500 focus:outline-none focus:ring-2 focus:ring-orange-500 md:text-base ${
                           errors.company ? "border-red-500 focus:border-red-500 focus:ring-red-500" : "border-gray-300"
                        }`}
                        placeholder="Company Name"
                        autoComplete="organization"
                        aria-invalid={errors.company ? "true" : "false"}
                        aria-describedby={errors.company ? "company-error" : undefined}
                     />
                     {errors.company && (
                        <span id="company-error" className="mt-1 text-left text-sm text-red-500" role="alert">
                           {errors.company.message}
                        </span>
                     )}
                  </div>
                  <div className="flex-1">
                     <label className="mb-1 block text-start text-xs font-medium text-gray-700" htmlFor="companyUrl">
                        Company URL
                     </label>
                     <input
                        id="companyUrl"
                        {...register("companyUrl", { required: "Company URL is required" })}
                        className={`block w-full rounded-md border px-3 py-2 text-sm text-gray-900 placeholder-gray-400 focus:border-orange-500 focus:outline-none focus:ring-2 focus:ring-orange-500 md:text-base ${
                           errors.companyUrl
                              ? "border-red-500 focus:border-red-500 focus:ring-red-500"
                              : "border-gray-300"
                        }`}
                        placeholder="https://yourcompany.com"
                        autoComplete="url"
                        aria-invalid={errors.companyUrl ? "true" : "false"}
                        aria-describedby={errors.companyUrl ? "companyUrl-error" : undefined}
                     />
                     {errors.companyUrl && (
                        <span id="companyUrl-error" className="mt-1 text-left text-sm text-red-500" role="alert">
                           {errors.companyUrl.message}
                        </span>
                     )}
                  </div>
               </div>
               <div>
                  <label className="mb-1 block text-start text-xs font-medium text-gray-700" htmlFor="phone">
                     Phone
                  </label>
                  <input
                     id="phone"
                     {...register("phone", { required: "Phone is required" })}
                     className={`block w-full rounded-md border px-3 py-2 text-sm text-gray-900 placeholder-gray-400 focus:border-orange-500 focus:outline-none focus:ring-2 focus:ring-orange-500 md:text-base ${
                        errors.phone ? "border-red-500 focus:border-red-500 focus:ring-red-500" : "border-gray-300"
                     }`}
                     placeholder="(555) 555-5555"
                     autoComplete="tel"
                     aria-invalid={errors.phone ? "true" : "false"}
                     aria-describedby={errors.phone ? "phone-error" : undefined}
                  />
                  {errors.phone && (
                     <span id="phone-error" className="mt-1 text-left text-sm text-red-500" role="alert">
                        {errors.phone.message}
                     </span>
                  )}
               </div>
               <div>
                  <label className="mb-1 block text-start text-xs font-medium text-gray-700" htmlFor="position">
                     Position
                  </label>
                  <input
                     id="position"
                     {...register("position", { required: "Position is required" })}
                     className={`block w-full rounded-md border px-3 py-2 text-sm text-gray-900 placeholder-gray-400 focus:border-orange-500 focus:outline-none focus:ring-2 focus:ring-orange-500 md:text-base ${
                        errors.position ? "border-red-500 focus:border-red-500 focus:ring-red-500" : "border-gray-300"
                     }`}
                     placeholder="Manager"
                     autoComplete="organization-title"
                     aria-invalid={errors.position ? "true" : "false"}
                     aria-describedby={errors.position ? "position-error" : undefined}
                  />
                  {errors.position && (
                     <span id="position-error" className="mt-1 text-left text-sm text-red-500" role="alert">
                        {errors.position.message}
                     </span>
                  )}
               </div>
               <div className="flex gap-4">
                  <div className="flex-1">
                     <label className="mb-1 block text-start text-xs font-medium text-gray-700" htmlFor="city">
                        City
                     </label>
                     <input
                        id="city"
                        {...register("city", { required: "City is required" })}
                        className={`block w-full rounded-md border px-3 py-2 text-sm text-gray-900 placeholder-gray-400 focus:border-orange-500 focus:outline-none focus:ring-2 focus:ring-orange-500 md:text-base ${
                           errors.city ? "border-red-500 focus:border-red-500 focus:ring-red-500" : "border-gray-300"
                        }`}
                        placeholder="City"
                        autoComplete="address-level2"
                        aria-invalid={errors.city ? "true" : "false"}
                        aria-describedby={errors.city ? "city-error" : undefined}
                     />
                     {errors.city && (
                        <span id="city-error" className="mt-1 text-left text-sm text-red-500" role="alert">
                           {errors.city.message}
                        </span>
                     )}
                  </div>
                  <div className="flex-1">
                     <label className="mb-1 block text-start text-xs font-medium text-gray-700" htmlFor="stateProvince">
                        State/Province
                     </label>
                     <select
                        id="stateProvince"
                        {...register("stateProvince", { required: "State/Province is required" })}
                        className={`block w-full rounded-md border px-3 py-2 text-sm text-gray-900 placeholder-gray-400 focus:border-orange-500 focus:outline-none focus:ring-2 focus:ring-orange-500 md:text-base ${
                           errors.stateProvince
                              ? "border-red-500 focus:border-red-500 focus:ring-red-500"
                              : "border-gray-300"
                        }`}
                        autoComplete="address-level1"
                        aria-invalid={errors.stateProvince ? "true" : "false"}
                        aria-describedby={errors.stateProvince ? "stateProvince-error" : undefined}
                     >
                        <option value="">Please Select...</option>
                        {states.map((option) => (
                           <option key={option} value={option}>
                              {option}
                           </option>
                        ))}
                     </select>
                     {errors.stateProvince && (
                        <span id="stateProvince-error" className="mt-1 text-left text-sm text-red-500" role="alert">
                           {errors.stateProvince.message}
                        </span>
                     )}
                  </div>
               </div>
               <div className="flex flex-col gap-4 md:flex-row">
                  <div className="flex-1 ">
                     <label
                        className="mb-1 block w-3/4 text-start text-xs font-medium text-gray-700"
                        htmlFor="companyOperations"
                     >
                        Which best describes your company’s operations? (pick one)
                     </label>
                     <select
                        id="companyOperations"
                        {...register("companyOperations", { required: "Company Operations is required" })}
                        className={`block w-full rounded-md border px-3 py-2 text-sm text-gray-900 placeholder-gray-400 focus:border-orange-500 focus:outline-none focus:ring-2 focus:ring-orange-500 md:text-base ${
                           errors.companyOperations
                              ? "border-red-500 focus:border-red-500 focus:ring-red-500"
                              : "border-gray-300"
                        }`}
                        aria-invalid={errors.companyOperations ? "true" : "false"}
                        aria-describedby={errors.companyOperations ? "companyOperations-error" : undefined}
                     >
                        <option value="">Please Select...</option>
                        {content.form.operationsOptions.map((option: string) => (
                           <option key={option} value={option}>
                              {option}
                           </option>
                        ))}
                     </select>
                     {errors.companyOperations && (
                        <span id="companyOperations-error" className="mt-1 text-left text-sm text-red-500" role="alert">
                           {errors.companyOperations.message}
                        </span>
                     )}
                  </div>
                  <div className="flex-1">
                     <label
                        className="mb-1 block w-3/4 text-start text-xs font-medium text-gray-700"
                        htmlFor="companyDescription"
                     >
                        What best describes your food service establishment? (pick one)
                     </label>
                     <select
                        id="companyDescription"
                        {...register("companyDescription", { required: "Company Description is required" })}
                        className={`block w-full rounded-md border px-3 py-2 text-sm text-gray-900 placeholder-gray-400 focus:border-orange-500 focus:outline-none focus:ring-2 focus:ring-orange-500 md:text-base ${
                           errors.companyDescription
                              ? "border-red-500 focus:border-red-500 focus:ring-red-500"
                              : "border-gray-300"
                        }`}
                        aria-invalid={errors.companyDescription ? "true" : "false"}
                        aria-describedby={errors.companyDescription ? "companyDescription-error" : undefined}
                     >
                        <option value="">Please Select...</option>
                        {content.form.descriptionOptions.map((option: string) => (
                           <option key={option} value={option}>
                              {option}
                           </option>
                        ))}
                     </select>
                     {errors.companyDescription && (
                        <span
                           id="companyDescription-error"
                           className="mt-1 text-left text-sm text-red-500"
                           role="alert"
                        >
                           {errors.companyDescription.message}
                        </span>
                     )}
                  </div>
               </div>
            </div>
            <button
               type="submit"
               className="!mt-2 block w-full rounded-md bg-orange-500 px-3 py-2 text-sm font-medium text-white no-underline hover:bg-orange-600 hover:text-white hover:no-underline disabled:cursor-not-allowed disabled:opacity-50 md:text-base"
               disabled={isLoading || isSubmitting}
            >
               Submit
            </button>
         </form>
      </SimpleModal>
   )
}

export default CompleteProfile
